import { baseGet } from "@/utils/api/base";
import apiEndPointList from "../constant/apiEndPointsList";
import { 
    AutotoursResponse, 
    AutotourDetailsResponse, 
    ProcessedAutotour, 
    ProcessedAutotourWithScenes,
    ProcessedAutotourScene,
    AutotourBase,
    AutotourWithDetails,
    PaginationResponse
} from "../../../../types/autotour";
import sublinkService from "./sublink";

const autotourServices = {
    getAllAutotours: async (id_path: string, page: string, limit: string, language_id: string): Promise<AutotoursResponse | null> => {
        try {
            const response = await baseGet<AutotoursResponse>({
                endpoint: apiEndPointList.autotour.GET_ALL_AUTOTOURS(id_path, page, limit, language_id),
                headers: {},
            });
            if (!response.data || !response.data.success) {
                console.error("Failed to fetch autotours:", response.data);
                return null;
            }
            return response.data;
        } catch (error) {
            console.error("Error fetching autotours:", error);
            return null;
        }
    },
        
    getAllAutoToursDetailsById: async (id_path: string, autotour_id: string, language_id: string): Promise<AutotourDetailsResponse | null> => {
        try {
            const response = await baseGet<AutotourDetailsResponse>({
                endpoint: apiEndPointList.autotour.GET_ALL_AUTOTOURS_DETAILS_BY_ID(id_path, autotour_id, language_id),
                headers: {},
            });
            if (!response.data || !response.data.success) {
                return null;
            }
            return response.data;
        } catch (error) {
            console.error("Error fetching autotour details by ID:", error);
            return null;
        }
    },

    processAutotours: (autotours: AutotourBase[]): ProcessedAutotour[] => {
        return autotours
            .filter(tour => tour.is_show !== 0 && tour.isVisible !== false)
            .map(tour => ({
                id: tour.id,
                title: tour.title || `Tour ${tour.id}`,
                thumbnail: tour.thumbnail || null, // Sửa từ "" thành null để fallback logic hoạt động đúng
                language_id: tour.language_id,
                count_scene: tour.count_scene || '0', 
                created_at: tour.created_at,
                updated_at: tour.updated_at,
                total_time: tour.total_time,
                audio: tour.audio,
                default_transition_config: tour.default_transition_config
            }));
    },

    processAutotourWithScenes: (autotour: AutotourWithDetails): ProcessedAutotourWithScenes => {
        const processedScenes: ProcessedAutotourScene[] = autotour.details.map(detail => ({
            id: detail.id,
            scene_id: detail.scene_id,
            scene_name: detail.scene_name,
            title: detail.title,
            scene_thumbnail: detail.scene_thumbnail,
            scene_thumbnail_original: detail.scene_thumbnail_original,
            hlookat: detail.hlookat,
            vlookat: detail.vlookat,
            fov: detail.fov,
            asset_id: detail.asset_id,
            asset_type_id: detail.asset_type_id,
            asset_display_duration_seconds: Number(detail.asset_display_duration_seconds) || null,
            asset_pause_mode: detail.asset_pause_mode || 'none',
            asset_video_autoplay: Boolean(Number(detail.asset_video_autoplay ?? 1)),
            asset_entrance_effect: detail.asset_entrance_effect || 'none',
            asset_title: detail.asset_title,
            asset_thumbnail: detail.asset_thumbnail,
            asset_model_url: detail.asset_model_url,
            is_show: detail.is_show === 1,
            // 🎬 Parse transition parameters từ string sang number
            transition_type: detail.transition_type || 'BLEND',
            transition_config: detail.transition_config,
            accel_speed: parseFloat(String(detail.accel_speed)) || 20,
            brake_speed: parseFloat(String(detail.brake_speed)) || -20,
            fov_speed: parseFloat(String(detail.fov_speed)) || 20,
            blend_time: parseFloat(String(detail.blend_time)) || 1.0,
            // 🎵 Narration cue parameters
            audio_offset: detail.audio_offset !== undefined && detail.audio_offset !== null ? Number(detail.audio_offset) : null,
            voice_offset: detail.voice_offset !== undefined && detail.voice_offset !== null ? Number(detail.voice_offset) : null,
            start_offset: detail.start_offset !== undefined && detail.start_offset !== null ? Number(detail.start_offset) : null,
            cue_time: detail.cue_time !== undefined && detail.cue_time !== null ? Number(detail.cue_time) : null,
            narration: detail.narration,
        }));

        return {
            id: autotour.id,
            title: autotour.title || `Tour ${autotour.id}`,
            thumbnail: autotour.thumbnail || null, // Sửa từ "" thành null để fallback logic hoạt động đúng
            language_id: autotour.language_id,
            scenes: processedScenes,
            created_at: autotour.created_at,
            updated_at: autotour.updated_at,
            audio: autotour.audio, // 🎵 Include audio URL from API
            total_time: autotour.total_time,
            default_transition_config: autotour.default_transition_config
        };
    },

    getProcessedAutotours: async (id_path: string, page: string, limit: string, language_id: string, sublink:string): Promise<{ tours: ProcessedAutotour[], pagination?: PaginationResponse }> => {
        let response:any;
        let link_id = sublink;
        if(isNaN(Number(sublink))){
            link_id = "1";
        }
        if(link_id == '1'){
            response = await autotourServices.getAllAutotours(id_path, page, limit, language_id);
        }
        else{
            response = await sublinkService.getAllAutotoursWithSublink(id_path, link_id, language_id, page, limit);
        }    
        if (response?.success && response.data) {
            const processedTours = autotourServices.processAutotours(response.data);
            return {
                tours: processedTours,
                pagination: response.pagination
            };
        }
        return { tours: [] };
    },

    getProcessedAutotourById: async (id_path: string, autotour_id: string, language_id: string): Promise<ProcessedAutotourWithScenes | null> => {
        const response = await autotourServices.getAllAutoToursDetailsById(id_path, autotour_id, language_id);
        if (response?.success && response.data) {
            if (response.data.is_show === 0 || response.data.isVisible === false) {
                return null;
            }
            return autotourServices.processAutotourWithScenes(response.data);
        }
        return null;
    },

    getScenesList: async (id_path: string, autotour_id: string, language_id: string): Promise<ProcessedAutotourScene[]> => {
        const tour = await autotourServices.getProcessedAutotourById(id_path, autotour_id, language_id);
        return tour?.scenes || [];
    },

    findTourById: async (id_path: string, tour_id: number, page: string, limit: string, sublink:string): Promise<ProcessedAutotour | null> => {
        const result = await autotourServices.getProcessedAutotours(id_path, page, limit, "", sublink);
        return result.tours.find((tour: ProcessedAutotour) => tour.id === tour_id) || null;
    },

    validateTourData: (tour: ProcessedAutotourWithScenes): boolean => {
        return !!(tour.id && tour.scenes && tour.scenes.length > 0);
    },

    calculateTourDuration: (scenes: ProcessedAutotourScene[]): number => {
        return scenes.reduce((total, scene) => {
            const baseTime = 2000; 
            const blendTime = scene.blend_time || 1.0;
            const fovFactor = Math.abs(scene.fov - 120) * 2; 
            return total + (baseTime * blendTime) + fovFactor;
        }, 0);
    }
}

export default autotourServices;
